import Layout from 'components/layout';

import { i18nFunct, localizePhoneNumbers } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Heading, Icon, IconType } from 'lucid-components-react';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import Footer from 'components/footer/footer';
import FooterNew from 'components/footer/footer-new';
import Link from 'components/link/link';
import TopHeroWithLogo from 'components/top-hero-with-logo/top-hero-with-logo';
import LucidContainer from 'components/lucid-container/lucid-container';
import Navbar from 'components/navbar/navbar';
import NavbarNew from 'components/navbar/navbar-new';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import { Drupal_NodeWhitePaper, Drupal_ParagraphTopHeroWithLogo } from 'generated/graphql';
import { useMenuFooter, useMenuTopNavigation } from 'hooks/use-menu';
import styled from 'theme/styled';
import { TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';
import linkParser from 'utils/link-parser';

const WhitePaper = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeWhitePaper;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;

  const i18n = i18nFunct(i18nData, i18nKey);

  const heroSection = node.fieldHeroSection
    ? (node.fieldHeroSection.entity as Drupal_ParagraphTopHeroWithLogo)
    : null;

  const heroCta = heroSection ? heroSection.fieldCtaLink : null;

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuFooter(langPrefix);

  const buttonGroupMx = 15;
  const buttonGroupMy = 10;
  const ButtonGroup = styled(Box)({
    display: 'inline-flex',
    flexWrap: 'wrap',
    margin: `-${buttonGroupMy}px 0 0 -${buttonGroupMx}px`,
    width: `calc(100% + ${buttonGroupMx}px)`,
    '> a.link': { margin: `${buttonGroupMy}px 0 0 ${buttonGroupMx}px` }
  });

  const isLegacy = node.fieldIsLegacyPage;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      {node.fieldHasNavbar && isLegacy ? (
        <Navbar fadeInTrigger=".homepage-hero">
          {node.fieldNavbarLink && (
            <Link
              className="button primary v2"
              to={node.fieldNavbarLink.url!.path!}
            >
              {node.fieldNavbarLink.title}
            </Link>
          )}
        </Navbar>
      )
        : node.fieldHasNavbar && <NavbarNew menu={navbarMenu} />}
      <LucidContainer
        as="main"
        css={theme => ({
          paddingTop: '40px',
          paddingBottom: '120px',
          [`@media screen and (max-width: ${theme.breakpoints.computer})`]: {
            paddingTop: '40px',
            paddingBottom: '40px'
          },
          [`@media screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
            paddingTop: '20px',
            paddingBottom: '20px'
          }
        })}
      >
        {isLegacy ? heroSection && (
          <TopHeroWithLogo
            small
            ctaLink={heroCta ? heroCta.url!.path! : ''}
            ctaText={heroCta ? heroCta.title! : ''}
            header={heroSection.fieldTitle!}
            paragraph={heroSection.fieldParagraph!}
            rightImage={heroSection.fieldImageLink!}
          />
        ) : <Heading variant="headlineXS">{node.fieldTitle}</Heading>}
        {node.fieldHasWhitepaperCta && (
          <Box as="section" className={`${pageType}__cta`}>
            <Heading as="h2" variant="subheadXS">
              {i18n('mktg-ct-cs-cta-header')}
            </Heading>
            <ButtonGroup className={`${pageType}__cta-header-buttons`}>
              <SignUpCta>{i18n('mktg-ct-og-feature-paragraph-cta')}</SignUpCta>
              <Link asButton to="/contact/get-a-quote">
                {i18n('mktg-ct-solutions-footer-cta-primary')}
              </Link>
            </ButtonGroup>
            <Box paddingY="20px">
              <Link
                className={`${pageType}__cta-phone`}
                to={localizePhoneNumbers('tel:+18772109890')}
              >
                {localizePhoneNumbers(
                  i18n('mktg-ct-solutions-footer-or-call', {
                    'sales-phone-number': '(877) 210 9890'
                  })
                )}
              </Link>
            </Box>
          </Box>
        )}
        {node.body && (
          <Box
            className={`${pageType}__body`}
            css={theme => ({
              width: '100%',
              [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                wordBreak: 'break-word'
              },
              tbody: {
                minWidth: '500px',
                overflow: 'scroll'
              },
              th: {
                background: theme.colors.coolGrey[20],
                textAlign: 'left'
              },
              td: {
                border: `1px solid ${theme.colors.coolGrey[30]}`,
                padding: '0 10px',
                [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                  fontSize: '0.5rem',
                  padding: '0 5px',
                  wordBreak: 'normal'
                }
              },
              'ol, ul': {
                '> li': {
                  marginBottom: '10px'
                },
                'ul, ol': {
                  padding: `${theme.space[3]}px ${theme.space[5]}px`
                }
              }
            })}
          >
            {ReactHtmlParser(getProcessedValue(node.body), {
              transform: linkParser
            })}
          </Box>
        )}
        {node.fieldHasRssLink && (
          <Box as="section" className={`${pageType}__rss`}>
            <Heading as="h2" mt={6} variant="subheadXS">
              {i18n('mtkg-whitepaper-rss')}
            </Heading>
            <Link asButton newTab primary to="/legal-rss.xml">
              <Icon type={IconType.RSS} />
              &nbsp;&nbsp;RSS
            </Link>
          </Box>
        )}
        {node.fieldHasWhitepaperCta && (
          <Box as="section" className={`${pageType}__cta`}>
            <Heading as="h2" mt={6} variant="subheadXS">
              {i18n('mktg-ct-cs-cta-header')}
            </Heading>
            <ButtonGroup className={`${pageType}__cta-header-buttons`}>
              <SignUpCta>{i18n('mktg-ct-og-feature-paragraph-cta')}</SignUpCta>
              <Link asButton secondary to="/contact/get-a-quote">
                {i18n('mktg-ct-solutions-footer-cta-primary')}
              </Link>
            </ButtonGroup>
            <Box paddingY="20px">
              <Link
                className={`${pageType}__cta-phone`}
                to={localizePhoneNumbers('tel:+18772109890')}
              >
                {localizePhoneNumbers(
                  i18n('mktg-ct-solutions-footer-or-call', {
                    'sales-phone-number': '(877) 210 9890'
                  })
                )}
              </Link>
            </Box>
          </Box>
        )}
      </LucidContainer>
      {
        node.fieldHasFooter && isLegacy ? <Footer /> : (
          node.fieldHasFooter && <FooterNew entityTranslations={entityTranslations} menu={footerMenu} />
        )
      }
    </Layout>
  );
};

export default WhitePaper;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeWhitePaper {
          ...entityFields
          fieldTitle
          fieldIsLegacyPage
          fieldNavbarLink {
            title
            url {
              path
            }
          }
          fieldHeroSection {
            entity {
              ... on Drupal_ParagraphTopHeroWithLogo {
                fieldTitle
                fieldImageLink
                fieldParagraph
                fieldCtaLink {
                  title
                  url {
                    path
                  }
                }
              }
            }
          }
          body {
            processed
          }
          fieldHasWhitepaperCta
          fieldHasNavbar
          fieldHasFooter
          fieldHasRssLink
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_cs_cta_header
            mktg_ct_og_feature_paragraph_cta
            mktg_ct_solutions_footer_cta_primary
            mktg_ct_solutions_footer_or_call
            mtkg_whitepaper_rss
          }
        }
      }
    }
  }
`;
